/**
 * Route Map Component
 * Displays per-vehicle-type, per-day route maps for a planning with TomTom + Leaflet.
 *
 * Day boundary = 05:00 — segments before 05:00 are counted towards the previous day
 * (overnight trips). Each vehicle-type within a day gets its own map + segment sequence.
 */

import React, { useState, useMemo } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Polyline, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import {
  Car, User, Map as MapIcon, Loader2, RefreshCw, Rocket, Flag, MapPin,
  Users2, Plane, TramFront, Ship, Bus, Bike, Calendar, ChevronDown, ChevronUp
} from 'lucide-react';
import { tomtomApi, PlanningRouteResult, SegmentRouteInfo, TomTomRoutePoint } from '../../services/tomtomApi';
import { VehicleType } from '../../types/enums';
import { PlanningSegment, VEHICLE_CONFIGS } from '../../types/matchdayPlanning';

// Fix for default marker icons in Leaflet with webpack/vite
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

// ── Marker icons ──

const createMarkerIcon = (color: string, isStart: boolean, isEnd: boolean) => {
  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="32" height="42" viewBox="0 0 32 42">
      <path d="M16 0C7.163 0 0 7.163 0 16c0 12 16 26 16 26s16-14 16-26C32 7.163 24.837 0 16 0z" fill="${color}"/>
      <circle cx="16" cy="16" r="10" fill="white"/>
      ${isStart ? '<polygon points="13,12 13,20 20,16" fill="' + color + '"/>' : ''}
      ${isEnd ? '<rect x="11" y="11" width="10" height="10" fill="' + color + '"/>' : ''}
      ${!isStart && !isEnd ? '<circle cx="16" cy="16" r="5" fill="' + color + '"/>' : ''}
    </svg>
  `;
  return L.divIcon({
    html: svg,
    className: 'custom-marker',
    iconSize: [32, 42],
    iconAnchor: [16, 42],
    popupAnchor: [0, -42]
  });
};

const startIcon = createMarkerIcon('#22c55e', true, false);
const endIcon = createMarkerIcon('#ef4444', false, true);
const waypointIcon = createMarkerIcon('#3b82f6', false, false);

const createPlaneIcon = (angle: number) => L.divIcon({
  html: `<svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="#6366f1" style="transform: rotate(${angle}deg);">
      <path d="M21 16v-2l-8-5V3.5c0-.83-.67-1.5-1.5-1.5S10 2.67 10 3.5V9l-8 5v2l8-2.5V19l-2 1.5V22l3.5-1 3.5 1v-1.5L13 19v-5.5l8 2.5z"/>
    </svg>`,
  className: 'plane-route-marker',
  iconSize: [28, 28],
  iconAnchor: [14, 14],
});

// ── Vehicle leg styles ──

interface VehicleLegStyle {
  color: string;
  dashArray?: string;
  straight: boolean;
  isPlane: boolean;
}

const getVehicleLegStyle = (vehicleType?: number): VehicleLegStyle => {
  switch (vehicleType) {
    case VehicleType.Plane:
      return { color: '#6366f1', dashArray: '8, 12', straight: true, isPlane: true };
    case VehicleType.Train:
      return { color: '#22c55e', dashArray: '10, 8', straight: false, isPlane: false };
    case VehicleType.Ship:
      return { color: '#3b82f6', dashArray: '10, 8', straight: true, isPlane: false };
    case VehicleType.Bicycle:
    case VehicleType.OnFoot:
      return { color: '#f59e0b', dashArray: '4, 8', straight: false, isPlane: false };
    default:
      return { color: '#1976d2', straight: false, isPlane: false };
  }
};

const getVehicleTypeIcon = (vehicleType?: number, size = 14) => {
  switch (vehicleType) {
    case VehicleType.Plane: return <Plane size={size} />;
    case VehicleType.Train: return <TramFront size={size} />;
    case VehicleType.Ship: return <Ship size={size} />;
    case VehicleType.Bus:
    case VehicleType.Minibus: return <Bus size={size} />;
    case VehicleType.Bicycle:
    case VehicleType.OnFoot: return <Bike size={size} />;
    default: return <Car size={size} />;
  }
};

// Map a string vehicle type from PlanningSegment to the numeric enum
const vehicleTypeStringToNum: Record<string, number> = {
  bus: VehicleType.Bus,
  minibus: VehicleType.Minibus,
  car: VehicleType.Car,
  plane: VehicleType.Plane,
  train: VehicleType.Train,
  taxi: VehicleType.Taxi,
  rentalcar: VehicleType.RentalCar,
  ship: VehicleType.Ship,
  bicycle: VehicleType.Bicycle,
  onfoot: VehicleType.OnFoot,
};

// ── Day boundary helper ──
// Day starts at 05:00 — anything before 05:00 belongs to the previous calendar day.
const DAY_START_HOUR = 5;

const getLogicalDay = (dateStr?: string, timeStr?: string): string => {
  if (!dateStr) return 'unbekannt';
  // Parse date
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;

  // Parse time
  if (timeStr) {
    const [hh, mm] = timeStr.split(':').map(Number);
    if (!isNaN(hh) && hh < DAY_START_HOUR) {
      // Before 05:00 → previous day
      d.setDate(d.getDate() - 1);
    }
  }
  // Return ISO date part
  return d.toISOString().slice(0, 10);
};

const formatDayLabel = (isoDate: string): string => {
  if (isoDate === 'unbekannt') return 'Ohne Datum';
  const d = new Date(isoDate + 'T12:00:00');
  const weekdays = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'];
  return `${weekdays[d.getDay()]}, ${d.getDate().toString().padStart(2, '0')}.${(d.getMonth() + 1).toString().padStart(2, '0')}.${d.getFullYear()}`;
};

// ── Vehicle type display name ──
const vehicleTypeNumToLabel: Record<number, string> = {
  [VehicleType.Bus]: 'Mannschaftsbus',
  [VehicleType.Minibus]: 'Transferbus',
  [VehicleType.Car]: 'KitCar',
  [VehicleType.Plane]: 'Flugzeug',
  [VehicleType.Train]: 'Bahn',
  [VehicleType.Taxi]: 'Taxi',
  [VehicleType.RentalCar]: 'Mietwagen',
  [VehicleType.Ship]: 'Schiff',
  [VehicleType.Bicycle]: 'Fahrrad',
  [VehicleType.OnFoot]: 'Zu Fuß',
};

const vehicleTypeNumToColor: Record<number, string> = {
  [VehicleType.Bus]: '#28a745',
  [VehicleType.Minibus]: '#ffc107',
  [VehicleType.Car]: '#17a2b8',
  [VehicleType.Plane]: '#6366f1',
  [VehicleType.Train]: '#8b5cf6',
  [VehicleType.Taxi]: '#f59e0b',
  [VehicleType.RentalCar]: '#0ea5e9',
  [VehicleType.Ship]: '#0891b2',
  [VehicleType.Bicycle]: '#22c55e',
  [VehicleType.OnFoot]: '#64748b',
};

// ── Map auto-fit helper ──

const FitBounds: React.FC<{ positions: [number, number][] }> = ({ positions }) => {
  const map = useMap();
  React.useEffect(() => {
    if (positions.length > 0) {
      const bounds = L.latLngBounds(positions.map(p => [p[0], p[1]]));
      map.fitBounds(bounds, { padding: [50, 50] });
    }
  }, [map, positions]);
  return null;
};

// ── Group type ──

interface SegmentGroup {
  day: string; // ISO date
  vehicleTypeNum: number;
  segments: SegmentRouteInfo[];
  totalDistanceKm: number;
  totalTimeMinutes: number;
}

// ── Single group card (map + sequence) ──

const GroupCard: React.FC<{ group: SegmentGroup; defaultOpen: boolean }> = ({ group, defaultOpen }) => {
  const [open, setOpen] = useState(defaultOpen);
  const label = vehicleTypeNumToLabel[group.vehicleTypeNum] || 'Sonstiges';
  const color = vehicleTypeNumToColor[group.vehicleTypeNum] || '#64748b';
  const style = getVehicleLegStyle(group.vehicleTypeNum);

  // All positions for bounding
  const allPositions = useMemo(() => {
    const pts: [number, number][] = [];
    for (const seg of group.segments) {
      if (seg.coordinates) pts.push([seg.coordinates.latitude, seg.coordinates.longitude]);
    }
    return pts;
  }, [group.segments]);

  return (
    <div style={{
      border: `2px solid ${color}`,
      borderRadius: '10px',
      overflow: 'hidden',
      marginBottom: '16px',
      backgroundColor: 'white'
    }}>
      {/* Group header */}
      <button
        type="button"
        onClick={() => setOpen(!open)}
        style={{
          width: '100%',
          padding: '12px 16px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '10px',
          backgroundColor: color + '15',
          border: 'none',
          cursor: 'pointer',
          borderBottom: open ? `1px solid ${color}40` : 'none'
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ color }}>{getVehicleTypeIcon(group.vehicleTypeNum, 20)}</span>
          <span style={{ fontWeight: 600, fontSize: '15px', color: '#333' }}>{label}</span>
          <span style={{
            fontSize: '12px',
            color: '#666',
            backgroundColor: '#f0f0f0',
            padding: '2px 8px',
            borderRadius: '10px'
          }}>
            {group.segments.length} Stopp{group.segments.length > 1 ? 's' : ''}
          </span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          {group.totalDistanceKm > 0 && (
            <span style={{ fontSize: '13px', color: '#555', fontWeight: 500 }}>
              {tomtomApi.formatDistance(group.totalDistanceKm)}
            </span>
          )}
          {group.totalTimeMinutes > 0 && (
            <span style={{ fontSize: '13px', color: '#555', fontWeight: 500 }}>
              {tomtomApi.formatTime(group.totalTimeMinutes)}
            </span>
          )}
          {open ? <ChevronUp size={18} color="#666" /> : <ChevronDown size={18} color="#666" />}
        </div>
      </button>

      {open && (
        <div style={{ padding: '16px' }}>
          {/* Mini map */}
          {allPositions.length >= 2 && (
            <div style={{
              marginBottom: '16px',
              borderRadius: '8px',
              overflow: 'hidden',
              border: '1px solid #e0e0e0',
              height: '300px'
            }}>
              <MapContainer
                center={allPositions[0]}
                zoom={10}
                style={{ height: '100%', width: '100%' }}
                scrollWheelZoom={true}
              >
                <TileLayer
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                <FitBounds positions={allPositions} />

                {/* Route legs */}
                {group.segments.slice(0, -1).map((seg, i) => {
                  const start = seg.coordinates;
                  const end = group.segments[i + 1]?.coordinates;
                  if (!start || !end) return null;

                  const positions: [number, number][] =
                    (!style.straight && seg.legPoints && seg.legPoints.length > 0)
                      ? seg.legPoints.map(p => [p.latitude, p.longitude] as [number, number])
                      : [[start.latitude, start.longitude], [end.latitude, end.longitude]];

                  const midLat = (start.latitude + end.latitude) / 2;
                  const midLon = (start.longitude + end.longitude) / 2;
                  const angle = Math.atan2(
                    end.longitude - start.longitude,
                    end.latitude - start.latitude
                  ) * (180 / Math.PI);

                  return (
                    <React.Fragment key={`leg-${i}`}>
                      <Polyline
                        positions={positions}
                        color={style.color}
                        weight={4}
                        opacity={0.85}
                        dashArray={style.dashArray}
                      />
                      {style.isPlane && (
                        <Marker
                          position={[midLat, midLon]}
                          icon={createPlaneIcon(angle)}
                          interactive={false}
                        />
                      )}
                    </React.Fragment>
                  );
                })}

                {/* Markers */}
                {allPositions.map((pos, index) => {
                  const isFirst = index === 0;
                  const isLast = index === allPositions.length - 1;
                  const icon = isFirst ? startIcon : isLast ? endIcon : waypointIcon;
                  const seg = group.segments[index];

                  return (
                    <Marker key={`m-${index}`} position={pos} icon={icon}>
                      <Popup>
                        <div style={{ fontSize: '14px' }}>
                          <strong>
                            {isFirst ? <><Rocket size={14} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 4 }} /> Start</>
                              : isLast ? <><Flag size={14} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 4 }} /> Ziel</>
                              : <><MapPin size={14} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 4 }} /> Stopp {index}</>}
                          </strong>
                          <br />
                          {seg?.location}
                        </div>
                      </Popup>
                    </Marker>
                  );
                })}
              </MapContainer>
            </div>
          )}

          {/* Sequence / timeline */}
          <div>
            {group.segments.map((seg, index) => {
              const isLast = index === group.segments.length - 1;
              const markerColor = index === 0 ? '#22c55e' : isLast ? '#ef4444' : color;

              return (
                <div key={seg.segmentId} style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                  {/* Step marker */}
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <div style={{
                      width: '28px', height: '28px', borderRadius: '50%',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      color: 'white', fontWeight: 'bold', fontSize: '13px',
                      backgroundColor: markerColor
                    }}>
                      {index + 1}
                    </div>
                    {!isLast && (
                      <div style={{ width: '2px', height: '40px', backgroundColor: '#ddd', margin: '4px 0' }} />
                    )}
                  </div>

                  {/* Info */}
                  <div style={{ flex: 1, paddingBottom: isLast ? 0 : '12px' }}>
                    <div style={{ fontWeight: 500, color: '#333', fontSize: '14px' }}>{seg.location}</div>
                    {seg.coordinates && (
                      <div style={{ fontSize: '11px', color: '#aaa', marginTop: '2px' }}>
                        {seg.coordinates.latitude.toFixed(4)}, {seg.coordinates.longitude.toFixed(4)}
                      </div>
                    )}

                    {/* Meta badges */}
                    <div style={{ marginTop: '6px', display: 'flex', flexWrap: 'wrap', gap: '6px', alignItems: 'center' }}>
                      {seg.vehicleName && (
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '3px', padding: '2px 6px', backgroundColor: '#f0f0f0', borderRadius: '4px', fontSize: '11px', color: '#555' }}>
                          <Car size={10} /> {seg.vehicleName}
                        </span>
                      )}
                      {seg.driverName && (
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '3px', padding: '2px 6px', backgroundColor: '#e3f2fd', borderRadius: '4px', fontSize: '11px', color: '#1976d2' }}>
                          <User size={10} /> {seg.driverName}
                        </span>
                      )}
                      {seg.partnerName && (
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '3px', padding: '2px 6px', backgroundColor: '#fff3e0', borderRadius: '4px', fontSize: '11px', color: '#e65100' }}>
                          <Users2 size={10} /> {seg.partnerName}
                        </span>
                      )}
                    </div>

                    {/* Distance / time to next */}
                    {!isLast && seg.distanceToNextKm !== undefined && seg.timeToNextMinutes !== undefined && (
                      <div style={{ marginTop: '6px', fontSize: '13px', color, display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <span>↓ {tomtomApi.formatDistance(seg.distanceToNextKm)}</span>
                        <span>•</span>
                        <span>{tomtomApi.formatTime(seg.timeToNextMinutes)}</span>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

// ── Main component ──

interface RouteMapProps {
  planningId: number;
  /** Planning segments — needed for scheduledDate/scheduledTime to split by day */
  segments?: PlanningSegment[];
  onRouteCalculated?: (result: PlanningRouteResult) => void;
}

const RouteMap: React.FC<RouteMapProps> = ({ planningId, segments: planningSegments, onRouteCalculated }) => {
  const [routeResult, setRouteResult] = useState<PlanningRouteResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const calculateRoute = async () => {
    if (!planningId) return;
    setLoading(true);
    setError(null);
    try {
      const result = await tomtomApi.calculatePlanningRoute(planningId);
      setRouteResult(result);
      onRouteCalculated?.(result);
    } catch (err: any) {
      console.error('Failed to calculate route:', err);
      setError(err.response?.data?.message || err.message || 'Routenberechnung fehlgeschlagen');
    } finally {
      setLoading(false);
    }
  };

  // Build a map from segment id → PlanningSegment for date/time lookup
  const segmentLookup = useMemo(() => {
    const map = new Map<string, PlanningSegment>();
    if (planningSegments) {
      for (const s of planningSegments) {
        map.set(String(s.id), s);
      }
    }
    return map;
  }, [planningSegments]);

  // Group route segments by logical day + vehicle type
  const grouped: SegmentGroup[] = useMemo(() => {
    if (!routeResult) return [];

    const segs = routeResult.segments;
    if (!segs || segs.length === 0) return [];

    // Build intermediate entries with day + vehicleType
    interface Entry {
      seg: SegmentRouteInfo;
      day: string;
      vt: number;
    }

    const entries: Entry[] = segs.map(seg => {
      const ps = segmentLookup.get(seg.segmentId);
      const day = getLogicalDay(ps?.scheduledDate, ps?.scheduledTime);
      const vt = seg.vehicleType ?? vehicleTypeStringToNum[ps?.vehicleType ?? ''] ?? -1;
      return { seg, day, vt };
    });

    // Group into contiguous runs of same day+vehicleType.
    // Adjacent segments with the same day+vt belong together.
    // When the vehicleType or day changes, start a new group.
    // The LAST segment of a group is also the FIRST of the next (as transition point).
    const groups: SegmentGroup[] = [];
    let currentDay = entries[0].day;
    let currentVt = entries[0].vt;
    let currentSegs: SegmentRouteInfo[] = [entries[0].seg];

    for (let i = 1; i < entries.length; i++) {
      const e = entries[i];
      if (e.day !== currentDay || e.vt !== currentVt) {
        // Finish current group
        groups.push(buildGroup(currentDay, currentVt, currentSegs));
        // Start new group — include previous last segment as transition start
        currentDay = e.day;
        currentVt = e.vt;
        currentSegs = [entries[i - 1].seg, e.seg];
      } else {
        currentSegs.push(e.seg);
      }
    }
    // Final group
    groups.push(buildGroup(currentDay, currentVt, currentSegs));

    return groups;
  }, [routeResult, segmentLookup]);

  // Collect unique days for day-headers
  const days = useMemo(() => {
    const seen = new Set<string>();
    const result: string[] = [];
    for (const g of grouped) {
      if (!seen.has(g.day)) {
        seen.add(g.day);
        result.push(g.day);
      }
    }
    return result;
  }, [grouped]);

  return (
    <div style={{
      backgroundColor: 'white',
      borderRadius: '8px',
      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
      overflow: 'hidden',
      marginTop: '20px'
    }}>
      <style>{`.plane-route-marker { background: transparent; border: none; } .custom-marker { background: transparent; border: none; }`}</style>

      {/* Header */}
      <div style={{
        padding: '16px 20px',
        backgroundColor: '#f8f9fa',
        borderBottom: '1px solid #e0e0e0',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <MapIcon size={20} />
          <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '600', color: '#333' }}>Routenübersicht</h3>
        </div>
        <button
          onClick={calculateRoute}
          disabled={loading}
          style={{
            padding: '8px 16px',
            fontSize: '14px',
            backgroundColor: loading ? '#ccc' : '#007bff',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            cursor: loading ? 'not-allowed' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            fontWeight: '500',
            transition: 'background-color 0.2s'
          }}
          onMouseEnter={(e) => { if (!loading) e.currentTarget.style.backgroundColor = '#0056b3'; }}
          onMouseLeave={(e) => { if (!loading) e.currentTarget.style.backgroundColor = '#007bff'; }}
        >
          {loading
            ? <><Loader2 size={14} /> Berechne...</>
            : <><RefreshCw size={14} /> Route berechnen</>}
        </button>
      </div>

      {/* Content */}
      <div style={{ padding: '20px' }}>
        {error && (
          <div style={{
            marginBottom: '16px', padding: '12px',
            backgroundColor: '#fee', border: '1px solid #fcc',
            borderRadius: '6px', color: '#c33', fontSize: '14px'
          }}>
            {error}
          </div>
        )}

        {!routeResult && !loading && !error && (
          <div style={{ textAlign: 'center', padding: '40px 20px', color: '#999' }}>
            <MapIcon size={48} />
            <p style={{ margin: '12px 0 0', fontSize: '14px' }}>
              Klicke auf "Route berechnen" um die tagesweise Routenübersicht anzuzeigen
            </p>
          </div>
        )}

        {routeResult && grouped.length > 0 && (
          <>
            {/* Global summary */}
            <div style={{
              marginBottom: '20px', padding: '14px',
              backgroundColor: '#e3f2fd', borderRadius: '8px',
              display: 'flex', alignItems: 'center', justifyContent: 'space-around'
            }}>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '22px', fontWeight: 'bold', color: '#1976d2' }}>
                  {routeResult.totalDistanceKm} km
                </div>
                <div style={{ fontSize: '12px', color: '#1976d2', marginTop: '2px' }}>Gesamt</div>
              </div>
              <div style={{ height: '28px', width: '1px', backgroundColor: '#90caf9' }} />
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '22px', fontWeight: 'bold', color: '#1976d2' }}>
                  {tomtomApi.formatTime(routeResult.totalTimeMinutes)}
                </div>
                <div style={{ fontSize: '12px', color: '#1976d2', marginTop: '2px' }}>Fahrzeit</div>
              </div>
              <div style={{ height: '28px', width: '1px', backgroundColor: '#90caf9' }} />
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '22px', fontWeight: 'bold', color: '#1976d2' }}>
                  {days.length} {days.length === 1 ? 'Tag' : 'Tage'}
                </div>
                <div style={{ fontSize: '12px', color: '#1976d2', marginTop: '2px' }}>
                  {grouped.length} {grouped.length === 1 ? 'Etappe' : 'Etappen'}
                </div>
              </div>
            </div>

            {/* Per-day sections */}
            {days.map((day, dayIdx) => {
              const dayGroups = grouped.filter(g => g.day === day);
              return (
                <div key={day} style={{ marginBottom: dayIdx < days.length - 1 ? '24px' : 0 }}>
                  {/* Day header */}
                  <div style={{
                    display: 'flex', alignItems: 'center', gap: '8px',
                    marginBottom: '12px', paddingBottom: '8px',
                    borderBottom: '2px solid #e0e0e0'
                  }}>
                    <Calendar size={18} color="#555" />
                    <h4 style={{ margin: 0, fontSize: '16px', fontWeight: 600, color: '#333' }}>
                      {formatDayLabel(day)}
                    </h4>
                    <span style={{
                      fontSize: '12px', color: '#888', marginLeft: 'auto'
                    }}>
                      {dayGroups.length} {dayGroups.length === 1 ? 'Verkehrsmittel' : 'Verkehrsmittel'}
                    </span>
                  </div>

                  {/* Vehicle-type cards for this day */}
                  {dayGroups.map((group, gIdx) => (
                    <GroupCard
                      key={`${day}-${group.vehicleTypeNum}-${gIdx}`}
                      group={group}
                      defaultOpen={dayGroups.length <= 2}
                    />
                  ))}
                </div>
              );
            })}
          </>
        )}

        {routeResult && grouped.length === 0 && (
          <div style={{ textAlign: 'center', padding: '20px', color: '#999', fontSize: '14px' }}>
            Keine Routendaten verfügbar. Bitte mindestens 2 Segmente mit Koordinaten anlegen.
          </div>
        )}
      </div>
    </div>
  );
};

// ── Helper ──

function buildGroup(day: string, vehicleTypeNum: number, segments: SegmentRouteInfo[]): SegmentGroup {
  let dist = 0;
  let time = 0;
  for (let i = 0; i < segments.length - 1; i++) {
    dist += segments[i].distanceToNextKm ?? 0;
    time += segments[i].timeToNextMinutes ?? 0;
  }
  return { day, vehicleTypeNum, segments, totalDistanceKm: Math.round(dist * 10) / 10, totalTimeMinutes: Math.round(time) };
}

export default RouteMap;
